"use client";

import { useState, useMemo, useEffect, useRef, useCallback, memo } from "react";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  useTheme,
  alpha,
  Paper,
  ClickAwayListener,
  Popper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Checkbox,
  DialogActions,
  Pagination,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  FilterAlt as Filter,
} from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import ClearIcon from '@mui/icons-material/Clear'
import AccountSettingServices from "../../../Api/AccountSettingServices/AccountSettingServices.index";
import { ErrorToaster } from "../../../Components/Toaster/Toaster";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../Context/UseContext";
import { CSVLink } from "react-csv";
import { Colors } from "../../../Assets/Styles/Colors";

// Memoized chip component to prevent unnecessary re-renders
const AttendanceChip = memo(
  ({ status, attendanceData, onChipClick, onRightClick, userRole }) => {
    const getChipColor = useCallback((status) => {
      switch (status) {
        case "Holiday":
          return { bg: "#f0f0f0", border: "grey" };
        case "WFH":
          return { bg: "#fee87f", border: "yellow" };
        case "Present":
          return { bg: "#daffd3", border: "green" };
        case "Absent":
          return { bg: "#fee6e4", border: "red" };
        default:
          return { bg: "#e6f2ff", border: "#e6f2ff" };
      }
    }, []);

    const colors = getChipColor(status);

    return (
      <Chip
        label={status}
        size="small"
        sx={{
          fontWeight: 500,
          borderRadius: 1,
          width: 100,
          py: 4,
          fontSize: "10px",
          backgroundColor: colors.bg,
          border: `1px solid ${colors.border}`,
          cursor: attendanceData ? "pointer" : "default",
        }}
        onClick={(e) =>
          attendanceData ? onChipClick(e, attendanceData) : null
        }
        onContextMenu={(e) => {
          if (userRole === "company" && attendanceData) {
            onRightClick(e, attendanceData);
          }
        }}
      />
    );
  }
);

// Memoized employee row component
const EmployeeRow = memo(
  ({
    employee,
    daysInMonth,
    selectedDate,
    onChipClick,
    onRightClick,
    userRole,
  }) => {
    const theme = useTheme();

    return (
      <TableRow
        hover
        sx={{
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: alpha(Colors.primary, 0.02),
          },
        }}
      >
        <TableCell
          sx={{
            position: "sticky",
            left: 0,
            zIndex: 1,
            bgcolor: theme.palette.background.paper,
            minWidth: 200,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={employee.avatar}
              alt={employee.name}
              sx={{ width: 40, height: 40 }}
            />
            <Typography variant="body1">{employee?.name}</Typography>
          </Box>
        </TableCell>
        {daysInMonth.map((day) => {
          const dateStr = day.format("YYYY-MM-DD");
          const isSunday = day.day() === 0;

          const attendanceForDay = employee.attendance?.find(
            (att) =>
              moment(att.date, "DD-MM-YYYY").format("YYYY-MM-DD") === dateStr
          );

          const status = isSunday? "Holiday": attendanceForDay?.is_wfh? "WFH": attendanceForDay?.status === "Present" ? attendanceForDay?.duration[0]?.check_out
              ? attendanceForDay?.duration[0]?.hours_worked
              : "Present"
            : "Absent";

          return (
            <TableCell key={dateStr} align="center" sx={{ py: 1 }}>
              <AttendanceChip
                status={status}
                attendanceData={attendanceForDay}
                onChipClick={onChipClick}
                onRightClick={onRightClick}
                userRole={userRole}
              />
            </TableCell>
          );
        })}
      </TableRow>
    );
  }
);

const getDaysInMonth = (selectedDate) => {
  const days = [];
  const year = selectedDate?.year();
  const month = selectedDate?.month();
  const daysInMonth = selectedDate?.daysInMonth();

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(moment([year, month, i]));
  }

  return days;
};

export default function OptimizedAttendanceTable() {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState(moment());
  const [userAttendanceData, setUserAttendanceData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popperData, setPopperData] = useState(null);
  const [nameFilter, setNameFilter] = useState("");
  const [csvLoading, setCsvLoading] = useState(false);
  const [disabledFields, setDisabledFields] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const csvLink = useRef();
  const { control, handleSubmit, reset, watch, setValue } = useForm();

  // Remove these mock lines:
  // const user = { role: "company" }
  // const state = { name: "Company" }

  // Add these real context hooks:
  const { state } = useLocation();
  const { user } = useAuth();

  // Memoized days calculation
  const daysInMonth = useMemo(
    () => getDaysInMonth(selectedDate),
    [selectedDate]
  );

  const getTimeReport = useCallback(async (month, year) => {
    try {
      setLoading(true);
      const response = await AccountSettingServices.getAllAttendance(
        month,
        year
      );
      setUserAttendanceData(response?.data);
    } catch (error) {
      console.log(error);
      ErrorToaster("Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoized data transformation
  const transformedData = useMemo(() => {
    return (
      userAttendanceData?.attendanceLog?.map((employee) => ({
        id: employee.user?._id,
        name: employee.user?.name,
        attendance: employee.attendance?.map((day) => ({
          date: moment(day.date).format("DD-MM-YYYY"),
          status:
            day.status === "present"
              ? "Present"
              : day.status === "absent"
              ? "Absent"
              : "Leave",
          duration: day.shifts,
          is_wfh: day?.is_wfh,
          total_shift_time: day?.total_shift_time,
          wfh_approval: day?.wfh_approval,
          wfh_reason: day?.wfh_reason,
          id: employee.user?._id,
          name: employee.user?.name,
        })),
      })) || []
    );
  }, [userAttendanceData]);

  // Memoized filtered data with pagination
  const { paginatedData, totalPages } = useMemo(() => {
    const filtered =
      transformedData?.filter((employee) =>
        employee.name.toLowerCase().includes(nameFilter.toLowerCase())
      ) || [];

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);

    return {
      paginatedData: paginated,
      totalPages: Math.ceil(filtered.length / itemsPerPage),
    };
  }, [transformedData, nameFilter, currentPage, itemsPerPage]);

  useEffect(() => {
    const fetchData = async () => {
      const month = selectedDate.format("MM");
      const year = selectedDate.format("YYYY");
      await getTimeReport(month, year);
    };
    fetchData();
  }, [selectedDate, getTimeReport]);

  useEffect(() => {
    if (csvData.length > 0) {
      csvLink?.current?.link?.click();
    }
  }, [csvData]);

  const handleChipClick = useCallback((event, data) => {
    console.log(data)
    setAnchorEl(event.currentTarget);
    setPopperData(data);
  }, []);

  const handlePopperClose = useCallback(() => {
    setAnchorEl(null);
    // setPopperData(null);
  }, []);

  const handleRightClick = useCallback(
    (e, data) => {
      e.preventDefault();
      setOpen(true);
      setValue(
        "check_in",
        data?.duration[0]?.check_in
          ? moment()
              .startOf("day")
              .add(data.duration[0].check_in, "minutes")
              .format("HH:mm")
          : ""
      );
      setValue(
        "check_out",
        data?.duration[0]?.check_out
          ? moment()
              .startOf("day")
              .add(data.duration[0].check_out, "minutes")
              .format("HH:mm")
          : ""
      );
      setValue("is_wfh", data?.is_wfh || false);
      setValue("wfh_reason", data?.wfh_reason || "");
      setValue("wfh_approval", data?.wfh_approval || "");
      setPopperData(data);
    },
    [setValue]
  );

  // const downloadCsv = useCallback(() => {
  //   setCsvLoading(true);
  //   try {
  //     const csvRows = [];

  //     transformedData?.forEach((employee) => {
  //       employee.attendance?.forEach((log) => {
  //         const durationFormatted =
  //           log.duration?.length > 0
  //             ? `${moment
  //                 .utc()
  //                 .startOf("day")
  //                 .add(log.duration[0]?.check_in || 0, "minutes")
  //                 .format("hh:mm A")} - ${moment
  //                 .utc()
  //                 .startOf("day")
  //                 .add(log.duration[0]?.check_out || 0, "minutes")
  //                 .format("hh:mm A")}`
  //             : "0";

  //         const totalWorked =
  //           log.duration?.length > 0 ? log.duration[0]?.hours_worked : "0";

  //         csvRows.push([
  //           log.name,
           
  //           log.date,
  //           log.status,
  //           log.is_wfh ? "Yes" : "No",
  //           log.wfh_approval || "-",
  //           log.wfh_reason || "-",
  //           durationFormatted,
  //           totalWorked,
  //         ]);
  //       });
  //     });

  //     const headers = [
  //       "Employee Name",
  //       "Date",
  //       "Status",
  //       "Is WFH",
  //       "WFH Approval",
  //       "WFH Reason",
  //       "Shift Duration",
  //       "Total Hours Worked",
  //     ];
  //     setCsvData([headers, ...csvRows]);
  //   } catch (error) {
  //     console.error("Error generating CSV:", error);
  //   } finally {
  //     setCsvLoading(false);
  //   }
  // }, [transformedData]);
  const downloadCsv = useCallback(() => {
    setCsvLoading(true);
    try {
      const employeeMap = new Map();
      const allDatesSet = new Set();
  
      transformedData?.forEach((employee) => {
        const empName = employee.name;
        if (!employeeMap.has(empName)) {
          employeeMap.set(empName, {});
        }
  
        employee.attendance?.forEach((log) => {
          allDatesSet.add(log.date);
  
          let status = log.status === "Present" ? "Present" : "Absent";
  
          if (status === "Present" && log.duration?.length > 0) {
            const checkIn = moment
              .utc()
              .startOf("day")
              .add(log.duration[0]?.check_in || 0, "minutes")
              .format("hh:mm A");
            const checkOut =log.duration[0]?.check_out ? moment
              .utc()
              .startOf("day")
              .add(log.duration[0]?.check_out || 0, "minutes")
              .format("hh:mm A") : "";
  
            status += ` (${checkIn} - ${checkOut})`;
          }
  
          employeeMap.get(empName)[log.date] = status;
        });
      });
  
      const allDates = Array.from(allDatesSet).sort();
      const headers = ["Employee Name", ...allDates];
      const csvRows = [];
  
      employeeMap.forEach((logsByDate, empName) => {
        const row = [empName];
        allDates.forEach((date) => {
          row.push(logsByDate[date] || "-");
        });
        csvRows.push(row);
      });
  
      setCsvData([headers, ...csvRows]);
    } catch (error) {
      console.error("Error generating CSV:", error);
    } finally {
      setCsvLoading(false);
    }
  }, [transformedData]);
  
  
  const onSubmit = useCallback(
    async (data) => {
      const isWfh = data.is_wfh;
      const checkInMinutes = isWfh
        ? null
        : data.check_in
        ? moment.duration(data.check_in).asMinutes()
        : null;
      const checkOutMinutes = isWfh
        ? null
        : data.check_out
        ? moment.duration(data.check_out).asMinutes()
        : null;

      if (!popperData?.date) {
        ErrorToaster(
          "Please select a date from the calendar to update attendance."
        );
        return;
      }

      const formattedData = {
        ...data,
        check_in: checkInMinutes,
        check_out: checkOutMinutes,
        user_id: popperData?.id,
        date: moment(popperData?.date, "DD-MM-YYYY").format("YYYY-MM-DD"),
      };

      try {
        const result = await AccountSettingServices.updateAttendance(
          formattedData
        );
        console.log("Submitted Attendance:", result);
        const month = selectedDate.format("MM");
        const year = selectedDate.format("YYYY");
        await getTimeReport(month, year);
      } catch (error) {
        ErrorToaster(error);
        console.log(error);
      } finally {
        setOpen(false);
      }
    },
    [popperData, selectedDate, getTimeReport]
  );

  const markAsWfh = watch("is_wfh");

  useEffect(() => {
    setDisabledFields(markAsWfh === true);
  }, [markAsWfh]);

  const formatDate = () => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date().toLocaleDateString("en-US", options);
  };

  return (
    <>
      <Box sx={{ margin: "0 auto", p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 700, color: Colors.primary, mb: 0.5 }}
            >
              Users Attendance List
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CalendarIcon fontSize="small" />
              {formatDate()}
            </Typography>
          </Box>
        </Box>

        {/* Filters */}
        {user?.role === "company" && (
          <>
            <Card
              elevation={0}
              sx={{
                mb: 4,
                borderRadius: 2,
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Filter fontSize="small" color="primary" />
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ ml: 1, fontWeight: 600 }}
                  >
                    Filter Attendance Records
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Select Month"
                      type="month"
                      variant="outlined"
                      size="small"
                      value={selectedDate.format("YYYY-MM")}
                      onChange={(e) => {
                        setSelectedDate(moment(e.target.value, "YYYY-MM"));
                        setCurrentPage(1); // Reset pagination
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Search by Name"
                      variant="outlined"
                      size="small"
                      value={nameFilter}
                      onChange={(e) => {
                        setNameFilter(e.target.value);
                        setCurrentPage(1); // Reset pagination
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {transformedData.length}{" "}
                employees
              </Typography>
              <Button
                variant="contained"
                onClick={downloadCsv}
                disabled={csvLoading}
                sx={{
                  minWidth: 150,
                  backgroundColor: Colors.primary,
                  color: Colors.white,
                  ":hover": {
                    backgroundColor: Colors.primary,
                    color: Colors.white,
                  },
                }}
              >
                {csvLoading ? "Generating..." : "Download Excel"}
              </Button>
            </Box>
          </>
        )}

        {/* Table */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          {loading && <LinearProgress />}
          <TableContainer sx={{ maxHeight: 600, overflow: "auto" }}>
            <Table stickyHeader aria-label="attendance table">
              <TableHead>
                <TableRow>
                <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      color: Colors.primary,
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      minWidth: 200,
                      background: "rgb(243 248 253)",
                    }}
                  >
                    Employee Name
                  </TableCell>
                  {daysInMonth.map((day) => (
                    <TableCell
                    key={day.format("D")}
                    align="center"
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      color: Colors.primary,
                      minWidth: 100,
                      padding: "8px 4px",
                      border: "none !important",
                      fontSize: "0.75rem",
                      lineHeight: 2,
                      whiteSpace: "nowrap",
                      zIndex: 0,
                      zIndex: 1,
                      background: "rgb(243 248 253)",
                    }}
                  >
                    {day.format("D")} <br />
                    {day.format("ddd")}
                  </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData?.length > 0 ? (
                  paginatedData.map((employee) => (
                    <EmployeeRow
                      key={employee.id}
                      employee={employee}
                      daysInMonth={daysInMonth}
                      selectedDate={selectedDate}
                      onChipClick={handleChipClick}
                      onRightClick={handleRightClick}
                      userRole={user?.role}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={daysInMonth.length + 1}
                      align="center"
                      sx={{ py: 6 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Filter
                          fontSize="large"
                          sx={{ color: alpha(theme.palette.text.primary, 0.2) }}
                        />
                        <Typography variant="h6" color="text.secondary">
                          No matching records found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try adjusting your filters to find what you're looking
                          for
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-textPrimary":{
                    backgroundColor: "#fee87f",
                  },
                  "& .Mui-selected":{
                    backgroundColor: Colors.primary,
                  }

                }}
              />
            </Box>
          )}
        </Card>
      </Box>

      {/* Popper for attendance details */}
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="top"
        disablePortal
        sx={{
          zIndex:4
        }}
      >
        <ClickAwayListener onClickAway={handlePopperClose}>
          <Paper
            sx={{
              p: 2,
              border: "1px solid #ddd",
              boxShadow: 3,
              minWidth: 200,
              backgroundColor: "#fff",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Date:{" "}
              {popperData?.date &&
                moment(popperData.date, "DD-MM-YYYY").format("DD-MM-YYYY")}
            </Typography>
            {popperData?.is_wfh ? (
              <>
                <Typography variant="body2">
                  WFH Approval: {popperData?.wfh_approval || ""}
                </Typography>
                <Typography variant="body2">
                  WFH Reason: {popperData?.wfh_reason || ""}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2">
                  Check-in:{" "}
                  {popperData?.duration[0]?.check_in != null
                    ? moment
                        .utc()
                        .startOf("day")
                        .add(popperData?.duration[0]?.check_in, "minutes")
                        .format("hh:mm A")
                    : "N/A"}
                </Typography>
                {console.log(popperData)}
                <Typography variant="body2">
                  Check-out:{" "}
                  {popperData?.duration[0]?.check_out != null
                    ? moment
                        .utc()
                        .startOf("day")
                        .add(popperData?.duration[0]?.check_out, "minutes")
                        .format("hh:mm A")
                    : "N/A"}
                </Typography>
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Edit Dialog */}
      <Dialog
        maxWidth="md"
        open={open}
        onClose={() => {
          setOpen(false);
          setDisabledFields(false);
          reset();
        }}
        sx={{
          "& .MuiDialog-paper": {
            width: "80%",
            height: "auto",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          Update Attendance for{" "}
          {moment(popperData?.date, "DD-MM-YYYY").format("DD-MM-YYYY")}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "15px !important",
          }}
        >
          {!disabledFields && (
           <>
           <Controller
             name="check_in"
             control={control}
             render={({ field, fieldState }) => (
               <TextField
                 {...field}
                 label="Check In"
                 type="time"
                 fullWidth
                 InputLabelProps={{ shrink: true }}
                 error={!!fieldState.error}
                 helperText={fieldState.error?.message}
                 InputProps={{
                   endAdornment: field.value ? (
                     <InputAdornment position="end">
                       <IconButton
                         onClick={() => field.onChange('')}
                         edge="end"
                       >
                         <ClearIcon />
                       </IconButton>
                     </InputAdornment>
                   ) : null,
                 }}
               />
             )}
           />
         
           <Controller
             name="check_out"
             control={control}
             render={({ field, fieldState }) => (
               <TextField
                 {...field}
                 label="Check Out"
                 type="time"
                 fullWidth
                 InputLabelProps={{ shrink: true }}
                 error={!!fieldState.error}
                 helperText={fieldState.error?.message}
                 InputProps={{
                   endAdornment: field.value ? (
                     <InputAdornment position="end">
                       <IconButton
                         onClick={() => field.onChange('')}
                         edge="end"
                       >
                         <ClearIcon />
                       </IconButton>
                     </InputAdornment>
                   ) : null,
                 }}
               />
             )}
           />
         </>
          )}
          <Controller
            name="is_wfh"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Mark as Work From Home"
              />
            )}
          />
          {disabledFields && (
            <>
              <Controller
                name="wfh_reason"
                control={control}
                rules={{ required: "WFH Reason is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="WFH Reason"
                    type="text"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="wfh_approval"
                control={control}
                rules={{ required: "WFH Approval is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="WFH Approval"
                    type="text"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setDisabledFields(false);
              reset();
            }}
            sx={{
              color: Colors.primary,
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)} sx={{
            background: Colors.primary,
            color:Colors.white,
            ":hover":{
              background: Colors.primary,
              color: Colors.white,
            }
          }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden CSV Link */}
      <CSVLink
        ref={csvLink}
        data={csvData}
        filename={`Attendance Report ${moment().format("DD-MMM-YYYY_HHmmss")}.csv`}
        target="_blank"
        style={{ display: "none" }}
      >
        Download CSV
      </CSVLink>
    </>
  );
}
